// Push reconciliation: PushPlan + what we last wrote + what the document
// holds now → the minimum set of ops, batched for short document locks.
//
// Three inputs, three questions:
//   • the PLAN (push-plan.ts) — what Live says should exist, keyed locally;
//   • the LEDGER — what Seedbed wrote last time, with the Audiotool ids and
//     the geometry it left each region at. Persisted by the extension, per
//     project;
//   • the DOC STATE — what the Audiotool document holds right now (built by
//     the extension's reader; plain data here).
//
// THE MULTIPLAYER RULE. Audiotool is multiplayer; a human may be editing the
// same project in a browser. Seedbed only removes, moves or edits an entity it
// created AND that still looks exactly as it left it. Anything else it
// forgets — drops from the ledger, remembers as "yielded" — and never touches
// again. A yield lifts only when the thing changes in Live (a region's content
// fingerprint moves on), which is the user saying "push this again".
//
// The same rule covers the song: tempo and signature are pushed when LIVE's
// value changes since the last push, never merely because the document
// disagrees — a human may have set it. Project length only ever grows.
//
// MOVES. A region key is `${laneKey}@${positionTicks}`, so a clip moved in
// Live arrives as a new key and a vanished one. Leftovers on the same lane
// with the same content fingerprint are paired (nearest position first) into
// one in-place update — no upload, no remove, no re-insert (spike A1c).
//
// ORDERING. Batch 0 carries every song and track op, so tracks exist before
// any region names them; then removes, updates, creates — so a same-position
// replace never transiently doubles up — then track removals, after their
// regions. Batches are capped by weight (a note region weighs 1 + its note
// count) because `modify` holds the document lock for the whole batch.
//
// Uploads are listed separately: they run OUTSIDE any lock, and only for tags
// a region about to be created actually needs and the account library lacks.
//
// Pure: no I/O. Tested from workers/test/audiotool-reconcile.spec.ts.

import type {
  AudioRegionIntent,
  EnsureTrackIntent,
  LaneKey,
  NoteRegionIntent,
  PushPlan,
  RegionFingerprint,
  RegionKey,
} from "./push-plan.js";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type RegionIntent = AudioRegionIntent | NoteRegionIntent;

/** What Seedbed sets on a lane's track and mixer strip. */
export interface TrackAttrs {
  displayName: string;
  muted: boolean;
  soloed: boolean;
  /** Strip postGain, linear. */
  gain: number;
  panning: number;
}

export interface LedgerTrack {
  trackId: string;
  trackKind: "audio" | "note";
  order: number;
  /** As Seedbed last WROTE them — a human's strip moves are never reverted. */
  attrs: TrackAttrs;
  /** The track's player device and mixer strip, for the writer. */
  playerId?: string;
  stripId?: string;
}

export interface LedgerRegion {
  key: RegionKey;
  laneKey: LaneKey;
  kind: "audio" | "note";
  regionId: string;
  fp: RegionFingerprint;
  /** Geometry as WE last wrote it — the "is this still ours?" probe. */
  observed: { positionTicks: number; durationTicks: number };
}

export interface PushLedger {
  projectId: string;
  /** Song values as Seedbed last WROTE them (null: never written). */
  song: {
    tempoBpm: number | null;
    signature: { numerator: number; denominator: number } | null;
  };
  tracks: Record<LaneKey, LedgerTrack>;
  regions: Record<RegionKey, LedgerRegion>;
  /** Keys a human took over, with the content fingerprint at that moment. */
  yieldedRegions: Record<RegionKey, string>;
  /** Lanes whose track a human deleted. */
  yieldedLanes: Record<LaneKey, true>;
}

/** An uploaded, transcoded sample — what insertSample needs to place it. */
export interface LibrarySample {
  /** `samples/<uuid>`. */
  name: string;
  durationSeconds: number;
}

/** Account-scoped: a content tag is uploaded once, ever. */
export interface SampleLibrary {
  byTag: Record<string, LibrarySample>;
}

/** The document right now — EVERY audio/note track and region, ours or not. */
export interface DocState {
  config: {
    tempoBpm: number;
    signatureNumerator: number;
    signatureDenominator: number;
    durationTicks: number;
  } | null;
  tracks: Record<string, { orderAmongTracks: number; regionIds: string[] }>;
  regions: Record<string, { positionTicks: number; durationTicks: number; trackId: string }>;
}

export function emptyLedger(projectId: string): PushLedger {
  return {
    projectId,
    song: { tempoBpm: null, signature: null },
    tracks: {},
    regions: {},
    yieldedRegions: {},
    yieldedLanes: {},
  };
}

// ---------------------------------------------------------------------------
// Ops
// ---------------------------------------------------------------------------

export type PushOp =
  | { op: "ensureConfig"; tempoBpm: number }
  | { op: "setTempo"; bpm: number }
  | { op: "setSignature"; numerator: number; denominator: number }
  | { op: "growDuration"; durationTicks: number }
  | { op: "createTrack"; laneKey: LaneKey; trackKind: "audio" | "note"; order: number; attrs: TrackAttrs }
  | { op: "updateTrack"; laneKey: LaneKey; trackId: string; attrs: TrackAttrs }
  | { op: "removeTrack"; laneKey: LaneKey; trackId: string }
  | { op: "forgetTrack"; laneKey: LaneKey; reason: "human-deleted" | "has-foreign-regions" }
  | { op: "createRegion"; intent: RegionIntent }
  /** In-place: geometry and/or attrs. `fromKey !== intent.key` for a move. */
  | { op: "updateRegion"; fromKey: RegionKey; regionId: string; intent: RegionIntent }
  | { op: "removeRegion"; key: RegionKey; regionId: string }
  | { op: "forgetRegion"; key: RegionKey; reason: "human-moved" | "human-deleted"; contentFp: string };

export interface PushUpload {
  tag: string;
  displayName: string;
}

export type PushNoticeCode = "tempo-yielded" | "signature-yielded" | "region-yielded" | "lane-yielded" | "track-kept";

export interface PushNotice {
  code: PushNoticeCode;
  detail: string;
  count: number;
}

export interface PushDelta {
  /** Run BEFORE the batches, outside any document lock. */
  uploads: PushUpload[];
  /** One document transaction per batch, in order. */
  batches: PushOp[][];
  notices: PushNotice[];
}

export interface ReconcileOptions {
  /** Cap on a batch's weight; a note region weighs 1 + its note count. */
  maxOpsPerBatch?: number;
}

const NOTICE_TEXT: Record<PushNoticeCode, string> = {
  "tempo-yielded": "Someone changed the tempo in Audiotool; it will stay until you change it in Live.",
  "signature-yielded": "Someone changed the time signature in Audiotool; it was left alone.",
  "region-yielded": "Someone moved or deleted clips in Audiotool; Seedbed left those alone.",
  "lane-yielded": "Someone deleted a track in Audiotool; Seedbed stopped pushing that lane.",
  "track-kept": "A lane you deleted still has someone else's clips in Audiotool, so its track was kept.",
};

class Notices {
  private readonly counts = new Map<PushNoticeCode, number>();
  add(code: PushNoticeCode): void {
    this.counts.set(code, (this.counts.get(code) ?? 0) + 1);
  }
  list(): PushNotice[] {
    return [...this.counts].map(([code, count]) => ({ code, detail: NOTICE_TEXT[code], count }));
  }
}

// ---------------------------------------------------------------------------

export function reconcilePush(
  plan: PushPlan,
  ledger: PushLedger,
  doc: DocState,
  library: SampleLibrary,
  opts: ReconcileOptions = {},
): PushDelta {
  const notices = new Notices();
  const head: PushOp[] = [];
  const removes: PushOp[] = [];
  const updates: PushOp[] = [];
  const creates: PushOp[] = [];
  const tail: PushOp[] = [];

  const plannedTracks = new Map<LaneKey, EnsureTrackIntent>();
  const plannedRegions = new Map<RegionKey, RegionIntent>();
  const sampleNames = new Map<string, string>();
  let tempo: number | null = null;
  let signature: { numerator: number; denominator: number } | null = null;
  let minTicks = 0;
  for (const i of plan.intents) {
    if (i.kind === "setTempo") tempo = i.bpm;
    else if (i.kind === "setSignature") signature = { numerator: i.numerator, denominator: i.denominator };
    else if (i.kind === "ensureDuration") minTicks = i.minTicks;
    else if (i.kind === "ensureTrack") plannedTracks.set(i.laneKey, i);
    else if (i.kind === "ensureSample") sampleNames.set(i.tag, i.displayName);
    else plannedRegions.set(i.key, i);
  }

  // ---- song ---------------------------------------------------------------
  if (!doc.config) {
    head.push({ op: "ensureConfig", tempoBpm: tempo ?? 120 });
    if (tempo !== null) head.push({ op: "setTempo", bpm: tempo });
    if (signature) head.push({ op: "setSignature", ...signature });
    if (minTicks > 0) head.push({ op: "growDuration", durationTicks: minTicks });
  } else {
    if (tempo !== null) {
      if (ledger.song.tempoBpm !== tempo) head.push({ op: "setTempo", bpm: tempo });
      else if (doc.config.tempoBpm !== tempo) notices.add("tempo-yielded");
    }
    if (signature) {
      const was = ledger.song.signature;
      if (!was || was.numerator !== signature.numerator || was.denominator !== signature.denominator) {
        head.push({ op: "setSignature", ...signature });
      } else if (
        doc.config.signatureNumerator !== signature.numerator ||
        doc.config.signatureDenominator !== signature.denominator
      ) {
        notices.add("signature-yielded");
      }
    }
    if (minTicks > doc.config.durationTicks) head.push({ op: "growDuration", durationTicks: minTicks });
  }

  // ---- which ledger entries are still ours ----------------------------------
  const yieldedLanes = new Set(Object.keys(ledger.yieldedLanes));
  const liveTracks = new Map<LaneKey, LedgerTrack>();
  for (const [laneKey, t] of Object.entries(ledger.tracks)) {
    if (doc.tracks[t.trackId]) {
      liveTracks.set(laneKey, t);
    } else {
      head.push({ op: "forgetTrack", laneKey, reason: "human-deleted" });
      yieldedLanes.add(laneKey);
      notices.add("lane-yielded");
    }
  }

  const yielded = new Map<RegionKey, string>(Object.entries(ledger.yieldedRegions));
  const ours = new Map<RegionKey, LedgerRegion>();
  for (const [key, r] of Object.entries(ledger.regions)) {
    const now = doc.regions[r.regionId];
    if (!now || yieldedLanes.has(r.laneKey)) {
      head.push({ op: "forgetRegion", key, reason: "human-deleted", contentFp: r.fp.content });
      yielded.set(key, r.fp.content);
      if (!yieldedLanes.has(r.laneKey)) notices.add("region-yielded");
    } else if (now.positionTicks !== r.observed.positionTicks || now.durationTicks !== r.observed.durationTicks) {
      head.push({ op: "forgetRegion", key, reason: "human-moved", contentFp: r.fp.content });
      yielded.set(key, r.fp.content);
      notices.add("region-yielded");
    } else {
      ours.set(key, r);
    }
  }

  // ---- tracks -------------------------------------------------------------
  const usedOrders = Object.values(doc.tracks).map((t) => t.orderAmongTracks);
  let nextOrder = usedOrders.length > 0 ? Math.max(...usedOrders) + 1 : 0;
  const sortedPlanned = [...plannedTracks.values()].sort((a, b) => a.ord - b.ord);
  for (const t of sortedPlanned) {
    if (yieldedLanes.has(t.laneKey)) continue;
    const attrs: TrackAttrs = { displayName: t.displayName, muted: t.muted, soloed: t.soloed, gain: t.gain, panning: t.panning };
    const have = liveTracks.get(t.laneKey);
    if (!have) {
      head.push({ op: "createTrack", laneKey: t.laneKey, trackKind: t.trackKind, order: nextOrder++, attrs });
    } else if (!sameAttrs(have.attrs, attrs)) {
      // Compared with what WE last wrote, not with the document: a human's
      // strip move stands until the user changes that lane in Live.
      head.push({ op: "updateTrack", laneKey: t.laneKey, trackId: have.trackId, attrs });
    }
  }

  // ---- regions --------------------------------------------------------------
  const unmatchedOld: LedgerRegion[] = [];
  const unmatchedNew: RegionIntent[] = [];
  const kindOf = (i: RegionIntent): "audio" | "note" => (i.kind === "audioRegion" ? "audio" : "note");

  for (const [key, r] of ours) {
    const want = plannedRegions.get(key);
    if (!want || kindOf(want) !== r.kind) {
      unmatchedOld.push(r);
      continue;
    }
    if (want.fp.content !== r.fp.content) {
      removes.push({ op: "removeRegion", key, regionId: r.regionId });
      creates.push({ op: "createRegion", intent: want });
    } else if (want.fp.geometry !== r.fp.geometry || want.fp.attrs !== r.fp.attrs) {
      updates.push({ op: "updateRegion", fromKey: key, regionId: r.regionId, intent: want });
    }
  }
  for (const [key, want] of plannedRegions) {
    if (ours.has(key)) continue;
    if (yieldedLanes.has(want.laneKey)) continue;
    if (yielded.get(key) === want.fp.content) continue; // the human still owns it
    unmatchedNew.push(want);
  }

  // Pair leftovers into moves: same lane, same kind, same content; nearest first.
  const pool = [...unmatchedOld];
  for (const want of unmatchedNew.sort((a, b) => a.positionTicks - b.positionTicks)) {
    let best = -1;
    let bestDist = Infinity;
    pool.forEach((old, i) => {
      if (old.laneKey !== want.laneKey || old.kind !== kindOf(want) || old.fp.content !== want.fp.content) return;
      const d = Math.abs(old.observed.positionTicks - want.positionTicks);
      if (d < bestDist) {
        best = i;
        bestDist = d;
      }
    });
    if (best >= 0) {
      const old = pool.splice(best, 1)[0]!;
      updates.push({ op: "updateRegion", fromKey: old.key, regionId: old.regionId, intent: want });
    } else {
      creates.push({ op: "createRegion", intent: want });
    }
  }
  for (const old of pool) removes.push({ op: "removeRegion", key: old.key, regionId: old.regionId });

  // ---- lanes gone from Live -------------------------------------------------
  // Every region on the track is ours and was just queued for removal, or it
  // is someone else's — in which case the track stays and we forget it.
  const ourRegionIds = new Set([...ours.values()].map((r) => r.regionId));
  for (const [laneKey, t] of liveTracks) {
    if (plannedTracks.has(laneKey)) continue;
    const foreign = (doc.tracks[t.trackId]?.regionIds ?? []).some((id) => !ourRegionIds.has(id));
    if (foreign) {
      tail.push({ op: "forgetTrack", laneKey, reason: "has-foreign-regions" });
      notices.add("track-kept");
    } else {
      tail.push({ op: "removeTrack", laneKey, trackId: t.trackId });
    }
  }

  // ---- uploads ----------------------------------------------------------------
  const uploads: PushUpload[] = [];
  const seen = new Set<string>();
  for (const o of creates) {
    if (o.op !== "createRegion" || o.intent.kind !== "audioRegion") continue;
    const tag = o.intent.tag;
    if (seen.has(tag) || library.byTag[tag] !== undefined) continue;
    seen.add(tag);
    uploads.push({ tag, displayName: sampleNames.get(tag) ?? tag });
  }

  // ---- batches ------------------------------------------------------------------
  const cap = Math.max(1, opts.maxOpsPerBatch ?? 64);
  const batches: PushOp[][] = [];
  if (head.length > 0) batches.push(head);
  let current: PushOp[] = [];
  let weight = 0;
  for (const o of [...removes, ...updates, ...creates]) {
    const w = weightOf(o);
    if (current.length > 0 && weight + w > cap) {
      batches.push(current);
      current = [];
      weight = 0;
    }
    current.push(o);
    weight += w;
  }
  if (current.length > 0) batches.push(current);
  if (tail.length > 0) batches.push(tail);

  return { uploads, batches, notices: notices.list() };
}

/** Gains within 1e-6 are the same setting — a float round trip is not a change. */
function sameAttrs(a: TrackAttrs, b: TrackAttrs): boolean {
  return (
    a.displayName === b.displayName &&
    a.muted === b.muted &&
    a.soloed === b.soloed &&
    Math.abs(a.gain - b.gain) < 1e-6 &&
    Math.abs(a.panning - b.panning) < 1e-6
  );
}

function weightOf(o: PushOp): number {
  if (o.op === "createRegion" && o.intent.kind === "noteRegion") return 1 + o.intent.notes.length;
  return 1;
}
